<template>
  <rest-form-page
    :page-title="pageTitle"
    :fetch-endpoint="fetchEndpoint"
    :save-endpoint="saveEndpoint"
    :before-save="beforeSave"
    :after-save="afterSave"
    :rules="rules"
    :readonly="readonly"
    :model="model"
  >
    <template slot="form">
      <form-section>
        <el-row>
          <el-col :span="12">
            <rest-form-item
              prop="metabase"
              :label="$t('ProjectSettings_DataEngine')"
            >
              <toggle
                v-model="model.metabase"
                :active-text="
                  $t('ProjectSettings_CustomerIntelligence_Enabled')
                "
                :inactive-text="
                  $t('ProjectSettings_CustomerIntelligence_Disabled')
                "
              />
            </rest-form-item>

            <rest-form-item
              v-if="model.metabase"
              prop="metabase_collection_id"
              :label="$t('ProjectSettings_Metabase_Collection_ID')"
            >
              <el-input
                v-model="model.metabase_collection_id"
                :placeholder="$t('ProjectSettings_Metabase_Collection_ID')"
              />
            </rest-form-item>

            <rest-form-item
              prop="aggregates_enabled"
              :label="$t('ProjectSettings_Aggregates')"
            >
              <toggle
                v-model="model.aggregates_enabled"
                :active-text="
                  $t('ProjectSettings_CustomerIntelligence_Enabled')
                "
                :inactive-text="
                  $t('ProjectSettings_CustomerIntelligence_Disabled')
                "
              />
            </rest-form-item>
            <rest-form-item :label="$t('ProjectSettings_MLStudio_Enabled')">
              <toggle
                v-model="mlStudioModel.enabled"
                :active-text="
                  $t('ProjectSettings_CustomerIntelligence_Enabled')
                "
                :inactive-text="
                  $t('ProjectSettings_CustomerIntelligence_Disabled')
                "
                :disabled="!model.ml_studio"
              />
            </rest-form-item>
            <template v-if="mlStudioModel.enabled">
              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_TenantCode')"
              >
                <el-input
                  v-model="mlStudioModel.tenant.code"
                  :placeholder="
                    $t('ProjectSettings_MLStudio_TenantCode_Placeholder')
                  "
                />
              </rest-form-item>
              <rest-form-item :label="$t('ProjectSettings_MLStudio_PlanCode')">
                <el-select v-model="mlStudioModel.tenant.plan_code">
                  <el-option
                    v-for="plan in plans"
                    :value="plan.code"
                    :key="plan.code"
                  >
                    {{ plan.code }}
                  </el-option>
                </el-select>
              </rest-form-item>
              <rest-form-item :label="$t('ProjectSettings_MLStudio_PlanPaid')">
                <toggle v-model="mlStudioModel.tenant.plan_paid" />
              </rest-form-item>

              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_DatabaseType')"
              >
                <el-select
                  v-model="mlStudioModel.tenant.connection.databaseType"
                >
                  <el-option value="pg" label="Redshift / Postgres" />
                  <el-option value="mysql" label="MySQL" />
                  <el-option value="clickhouse" label="Clickhouse" />
                </el-select>
              </rest-form-item>

              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_DatabaseServer')"
              >
                <el-input
                  v-model="mlStudioModel.tenant.connection.databaseServer"
                />
              </rest-form-item>

              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_DatabasePort')"
              >
                <el-input
                  v-model="mlStudioModel.tenant.connection.databasePort"
                />
              </rest-form-item>

              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_DatabaseName')"
              >
                <el-input
                  v-model="mlStudioModel.tenant.connection.databaseName"
                />
              </rest-form-item>

              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_DatabaseUsername')"
              >
                <el-input
                  v-model="mlStudioModel.tenant.connection.databaseUsername"
                />
              </rest-form-item>

              <rest-form-item
                :label="$t('ProjectSettings_MLStudio_DatabasePassword')"
              >
                <el-input
                  v-model="mlStudioModel.tenant.connection.databasePassword"
                  type="password"
                />
              </rest-form-item>

              <rest-form-item :label="$t('ProjectSettings_MLStudio_UseSSL')">
                <toggle v-model="mlStudioModel.tenant.connection.useSSL" />
              </rest-form-item>
            </template>
          </el-col>
          <el-col :span="12">
            <rest-form-item
              prop="shopbox_enabled"
              :label="
                $t('ProjectSettings_ProductRecommendation_Feature_Switcher')
              "
            >
              <toggle
                v-model="model.shopbox_enabled"
                :active-text="
                  $t('ProjectSettings_CustomerIntelligence_Enabled')
                "
                :inactive-text="
                  $t('ProjectSettings_CustomerIntelligence_Disabled')
                "
              />
            </rest-form-item>

            <rest-form-item
              v-if="model.shopbox_enabled"
              prop="shopbox_url"
              :label="$t('ProjectSettings_ProductRecommendation_Shopbox_URL')"
            >
              <el-input
                v-model="model.shopbox_url"
                :placeholder="
                  $t('ProjectSettings_ProductRecommendation_Shopbox_URL')
                "
              />
            </rest-form-item>

            <rest-form-item
              v-if="model.shopbox_enabled"
              prop="shopbox_site_id"
              :label="$t('ProjectSettings_ProductRecommendation_SiteID')"
            >
              <el-input
                v-model="model.shopbox_site_id"
                :placeholder="
                  $t('ProjectSettings_ProductRecommendation_SiteID')
                "
              />
            </rest-form-item>

            <rest-form-item
              prop="customer_intelligence_lifecycle_overview"
              :label="
                $t('ProjectSettings_CustomerIntelligence_Lifecycle_Overview')
              "
            >
              <toggle
                v-model="model.customer_intelligence_lifecycle_overview"
                :active-text="
                  $t('ProjectSettings_CustomerIntelligence_Enabled')
                "
                :inactive-text="
                  $t('ProjectSettings_CustomerIntelligence_Disabled')
                "
              />
            </rest-form-item>

            <rest-form-item
              v-if="model.customer_intelligence_lifecycle_overview"
              prop="lifecycle_lapsing_period"
              :label="
                $t(
                  'ProjectSettings_CustomerIntelligence_Lifecycle_Period_Lapsing'
                )
              "
            >
              <el-input placeholder="30" />
            </rest-form-item>

            <rest-form-item
              v-if="model.customer_intelligence_lifecycle_overview"
              prop="lifecycle_churned_period"
              :label="
                $t(
                  'ProjectSettings_CustomerIntelligence_Lifecycle_Period_Churned'
                )
              "
            >
              <el-input placeholder="60" />
            </rest-form-item>

            <rest-form-item
              prop="customer_intelligence_templates"
              :label="$t('ProjectSettings_CustomerIntelligence_Templates')"
            >
              <toggle
                v-model="model.customer_intelligence_templates"
                :active-text="
                  $t('ProjectSettings_CustomerIntelligence_Enabled')
                "
                :inactive-text="
                  $t('ProjectSettings_CustomerIntelligence_Disabled')
                "
              />
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
    </template>
  </rest-form-page>
</template>

<script>
import RestFormPage from '@/common/components/rest/RestFormPage'
import FormSection from '@/common/components/form/FormSection'
import Toggle from '@/common/components/form/Toggle'
import RestFormItem from '@/common/components/rest/RestFormItem'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'CustomerIntelligencePage',
  components: {
    RestFormItem,
    Toggle,
    FormSection,
    RestFormPage
  },
  data() {
    return {
      model: {
        shopbox_enabled: 0,
        shopbox_url: null,
        shopbox_site_id: null,
        customer_intelligence_lifecycle_overview: 0,
        metabase: 0,
        metabase_collection_id: null,
        aggregates_enabled: 0,
        ml_studio: 0
      },
      mlStudioModel: {
        enabled: null,
        tenant: {
          code: null,
          plan_code: null,
          plan_paid: null,
          connection: {
            databaseType: null,
            databaseServer: null,
            databasePort: null,
            databaseName: null,
            databaseUsername: null,
            databasePassword: null,
            useSSL: null
          }
        }
      },
      plans: []
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.scrollTo(0, 0)
    })
  },
  computed: {
    pageTitle() {
      return this.$t(this.$route.meta.title)
    },
    pageBreadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('LeftNavigation_Advanced')
        },
        {
          label: this.pageTitle
        }
      ]
    },
    fetchEndpoint() {
      return [
        {
          method: 'get',
          url: `projects/${this.$route.query.project_id}`,
          handler: data => {
            this.model = data
            if (data.ml_studio) {
              RestApi(this.fetchMlSudioSettingsEndpoint).then(({ data }) => {
                this.mlStudioModel = {
                  enabled: data.enabled,
                  tenant: data.tenant
                }

                if (!this.mlStudioModel.tenant.connection) {
                  this.mlStudioModel.tenant.connection = {}
                }

                this.plans = data.plans

                if (data.error) {
                  this.$message.error(data.error)
                }
              })
            } else {
              this.mlStudioModel.enabled = false
            }
          }
        }
      ]
    },
    fetchMlSudioSettingsEndpoint() {
      return {
        method: 'get',
        url: `projects/${this.$route.query.project_id}/ml-studio/settings`
      }
    },
    saveEndpoint() {
      if (this.$route.query.project_id) {
        return {
          method: 'patch',
          url: `projects/${this.$route.query.project_id}`
        }
      } else {
        return {
          method: 'post',
          url: `projects`
        }
      }
    },
    readonly() {
      return !this.$isRole('admin')
    },
    rules() {
      return {
        shopbox_url: [
          {
            required: this.model.shopbox_enabled
          },
          {
            type: 'url',
            message: this.$t(
              'LeftNavigation_Product_Recommendation_Settings_Invalid_Https_Url'
            )
          },
          {
            validator: this.isUrlHttps
          }
        ],
        shopbox_site_id: {
          required: this.model.shopbox_enabled
        }
      }
    }
  },
  methods: {
    isUrlHttps(rule, value, callback) {
      if (!value.startsWith('https://')) {
        return callback(
          new Error(
            this.$t(
              'LeftNavigation_Product_Recommendation_Settings_Invalid_Https_Url'
            )
          )
        )
      } else {
        return callback()
      }
    },
    beforeSave() {
      if (this.model.metabase === 0 && this.mlStudioModel.enabled === 1) {
        const label = this.$t(
          'ProjectSettings_CustomerIntelligence_FlagError'
        ).toString()
        this.$message.error(label)
        throw new TypeError(label)
      }

      if (this.model.shopbox_enabled === 0) {
        this.model.shopbox_url = ''
        this.model.shopbox_site_id = ''
      }
    },
    afterSave(data, callback) {
      // save ml studio settings
      RestApi.post(
        `projects/${this.$route.query.project_id}/ml-studio/settings`,
        this.mlStudioModel
      )

      this.$store.dispatch('reloadProjectConfig')
      callback()

      // Reload whole page to invalidate the cache
      this.$router.go()
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/settings/pages/CustomerIntelligencePage.vue